"use client";

import type { FooterSocial } from "../../../types";
import { RuntimeLink } from "../../../runtime/RendererRuntime";
import { useDevPlaceholder } from "../../../runtime/DevPlaceholder";
import { isExternalHref, resolveConfiguredHref } from "../../../utils/links";
import { SocialBrandIcon } from "./SocialBrandIcon";

/**
 * Yellow-disc social row used by the footer (and structurally identical to
 * the header social row). Each link is a 36×36 yellow circle with a white
 * icon — visually consistent with the header so the brand reads as one
 * unified system.
 *
 * `bcc-footer-social` is added so the global stylesheet can constrain the
 * inner SVG sizing without us caring about each icon's intrinsic viewBox.
 *
 * The outer flex container accepts a {@link className} so callers can adjust
 * alignment and spacing per viewport (e.g., centered with extra padding on
 * mobile vs. left-aligned and tight on desktop).
 */
export function SocialDiscRow({ items, className }: { items: FooterSocial[]; className?: string }) {
	const { previewMode } = useDevPlaceholder();
	// Production hides social icons with no configured destination; preview keeps them.
	const visible = previewMode ? (items ?? []) : (items ?? []).filter((social) => resolveConfiguredHref(social.href));
	if (!visible.length) return null;
	const wrapperClass = ["flex flex-wrap gap-2", className].filter(Boolean).join(" ");
	return (
		<div className={wrapperClass}>

			{visible.map((social) => {
				const external = isExternalHref(social.href);
				const targetProps = external
					? ({ target: "_blank", rel: social.icon === "mail" ? undefined : "noreferrer" } as const)
					: ({} as const);
				return (
					<RuntimeLink
						key={`${social.icon}-${social.label}`}
						href={social.href}
						aria-label={social.label}
						className="bcc-footer-social grid h-9 w-9 place-items-center rounded-full bg-[#FFA401] text-white transition-opacity hover:opacity-90"
						{...targetProps}
					>
						<SocialBrandIcon type={social.icon} />
					</RuntimeLink>
				);
			})}
		</div>
	);
}
